import { Button, Card, Flex, Space, Spin, Table, TableColumnsType, Typography } from "antd";
import { getAllDocumentTypes } from "api/services/DocumentTypeService";
import { TDocumentType } from "api/services/models/types";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import { ReactComponent as AddDocumentType } from "Icons/vuesax/document.svg";
import { ReactComponent as EditIcon } from "Icons/vuesax/edit-2.svg";
import { useCallback, useEffect, useState } from "react";
import AddOrUpdateDocumentType from "./AddOrUpdateDocumentType";

type TModalDocumentTypeState = {
  documentType?: TDocumentType;
  showModal: boolean;
};

const composeColumns = (
  onEditDocumentType: (documentType: TDocumentType) => void
): TableColumnsType<TDocumentType> => {
  return [
    {
      title: "Document Type Id",
      dataIndex: "documentTypeId",
      key: "documentTypeId",
      sorter: (s1, s2) => s1.documentTypeId?.localeCompare(s2.documentTypeId)
    },
    {
      title: "Name",
      dataIndex: "documentTypeName",
      key: "documentTypeName",
      sorter: (s1, s2) => s1.documentTypeName?.localeCompare(s2.documentTypeName)
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      sorter: (s1, s2) => s1.description?.localeCompare(s2.description)
    },
    {
      title: "Phone Id",
      dataIndex: "phoneId",
      key: "phoneId",
      sorter: false
    },
    {
      title: "Client Phone Id",
      dataIndex: "clientPhoneId",
      key: "clientPhoneId",
      sorter: false
    },
    {
      title: "Client Document Type Id",
      dataIndex: "clientDocumentTypeId",
      key: "clientDocumentTypeId",
      sorter: false
    },
    {
      title: "Edit",
      key: "edit",
      dataIndex: "documentTypeId",
      render: (_, documentType) => (
        <EditIcon
          className="settings-edit-icon"
          onClick={() => onEditDocumentType(documentType)}
        />
      )
    }
  ];
};

const ManageDocumentTypes = () => {
  const { accountId } = useTDAuthentication();
  const { refresh } = usePicklists();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | AxiosError | null>();
  const [documentTypes, setDocumentTypes] = useState<TDocumentType[]>([]);
  const [editDocumentTypeState, setEditDocumentTypeState] = useState<TModalDocumentTypeState>({
    showModal: false
  });

  const [tableKey, setTableKey] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    if (accountId) {
      setLoading(true);
      setError(null);
      getAllDocumentTypes(accountId)
        .then(setDocumentTypes)
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, [accountId]);

  const editLocation = useCallback(
    (documentType: TDocumentType) => {
      setEditDocumentTypeState({ showModal: true, documentType });
    },
    [accountId, refresh]
  );

  const onEditDocumentTypeClose = (newOrUpdatedDocumentType?: TDocumentType) => {
    setEditDocumentTypeState({ showModal: false });
    if (newOrUpdatedDocumentType) {
      setDocumentTypes(types => {
        const index = types.findIndex(
          l => l.documentTypeId === newOrUpdatedDocumentType.documentTypeId
        );
        if (index === -1) {
          types.push(newOrUpdatedDocumentType);
        } else {
          types.splice(index, 1, newOrUpdatedDocumentType);
        }
        return [...types];
      });
      refresh?.("DocumentTypes");
    }
  };

  return (
    <Card
      style={{
        padding: "16px"
      }}
      className="default-border"
      styles={{ body: { padding: "0" } }}>
      <Flex wrap gap="middle" align="start" justify="space-between" style={{ marginBottom: "16px" }}>
        <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">Manage Document Types</Typography.Title>
        <Space size="small" direction="vertical" style={{ margin: 0 }} align="end">
          <Button
            type="primary"
            size="middle"
            className="default-button primary-btn"
            icon={<AddDocumentType className="default-icon" />}
            iconPosition={"start"}
            title="Add New Document Type"
            onClick={() => setEditDocumentTypeState({ showModal: true })}>
            <span>Add New Document Type</span>
          </Button>
          <span
            style={{
              fontSize: ".75rem"
            }}>{`Total Document Types: ${documentTypes.length}`}</span>
        </Space>
      </Flex>

      {loading && <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Loading, please wait..." />
        </Flex>}
      {error && <ErrorViewer error={error} />}
      {!loading &&
      <Card className="card-style" styles={{ body: { padding: 0 } }}>
      <Table<TDocumentType>
        key={tableKey}
        columns={composeColumns(editLocation)}
        rowKey={"documentTypeId"}
        dataSource={documentTypes.sort((a, b) => a.documentTypeName.localeCompare(b.documentTypeName))}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: {
            showSearch: false
          },
          pageSizeOptions: [5, 10, 20, 50, 100],
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size || 10);
          }
        }}
        className="table-style"
            components={{
              body: {
                cell: (props: any) => (
                  <td
                    {...props}
                    style={{
                      borderBottomWidth: "1px",
                      borderBottomColor: "#f0f0f0",
                      borderBottomStyle: "solid",
                      fontWeight: "500"
                    }}
                  />
                )
              }
            }}
            style={{ border: "none" }}
      />
      </Card>
      }
      {editDocumentTypeState.showModal && (
        <AddOrUpdateDocumentType
          onClose={onEditDocumentTypeClose}
          documentType={editDocumentTypeState.documentType}
        />
      )}
    </Card>
  );
};

export default ManageDocumentTypes;
